import { Message, SectionHeading, CircleLoader } from "../components";
import { ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { httpsCallable, FunctionsError } from "firebase/functions";
import { functions } from "../../config/firebase";
import { useSite } from "../../store";
import { ZipFile } from "./parts";
import { useTranslation } from "react-i18next";
import "./Download.scss";

export type ZipStatusValue = "none" | "refreshing" | "ready" | "failed";

export interface ZipStatusDoc {
    status: ZipStatusValue;
    path?: string;
    fileName?: string;
    fileCount?: number;
    sizeBytes?: number;
    downloadUrl?: string;
    dateCreated?: string;
    message?: string;
}

export interface FindZipResult extends ZipStatusDoc {
    originalFileCount: number;
    originalBytes?: number;
}

interface ZipCallableInput {
    siteId: string;
}

const findZipCallable = httpsCallable<ZipCallableInput, FindZipResult>(
    functions,
    "findZip"
);

export async function findZip(siteId: string): Promise<FindZipResult> {
    const { data } = await findZipCallable({ siteId });
    return data;
}

const POLL_INTERVAL_MS = 15000;

export const Download = () => {
    const { t } = useTranslation();

    const { siteId } = useSite();

    const [status, setStatus] = useState<ZipStatusDoc | null>(null);
    const [fetchError, setFetchError] = useState<string | null>(null);

    const pollTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const isMountedRef = useRef(true);

    const clearPoll = useCallback(() => {
        if (pollTimerRef.current) {
            clearTimeout(pollTimerRef.current);
            pollTimerRef.current = null;
        }
    }, []);

    const fetchStatus = useCallback(async () => {
        setFetchError(null);
        try {
            const data = await findZip(siteId);
            if (!isMountedRef.current) return;
            setStatus(data);
        } catch (e) {
            const err = e as FunctionsError;
            if (!isMountedRef.current) return;
            setFetchError(err?.message ?? t("download.fetchError", { error: "unknown" }));
        }
    }, [siteId, t]);

    // Initial load + cleanup
    useEffect(() => {
        isMountedRef.current = true;
        void fetchStatus();
        return () => {
            isMountedRef.current = false;
            clearPoll();
        };
    }, [fetchStatus, clearPoll]);

    // Poll while the owner's build is in flight so the page flips to
    // "ready" without a manual refresh. Guests never start a build
    // themselves — that happens in the admin dashboard.
    useEffect(() => {
        clearPoll();
        if (status?.status === "refreshing") {
            pollTimerRef.current = setTimeout(() => {
                void fetchStatus();
            }, POLL_INTERVAL_MS);
        }
        return clearPoll;
    }, [status, fetchStatus, clearPoll]);

    // Pick what to show: a loader while we don't yet know the state, the
    // ready card with its download button, or a single status message.
    let content: ReactNode;
    if (fetchError) {
        content = (
            <Message variant="error">
                {t("download.fetchError", { error: fetchError })}
            </Message>
        );
    } else if (!status) {
        content = <CircleLoader />;
    } else if (status.status === "ready") {
        content = <ZipFile status={status} />;
    } else if (status.status === "refreshing") {
        content = (
            <>
                <CircleLoader />
                <Message variant="primary">{t("download.createInfo")}</Message>
            </>
        );
    } else if (status.status === "failed") {
        content = (
            <Message variant="error">
                {t("download.failedLast", { error: status.message ?? "unknown" })}
            </Message>
        );
    } else {
        // status.status === "none": nothing has been built yet.
        content = <Message variant="primary">{t("download.noneYet")}</Message>;
    }

    return (
        <div className="body">
            <SectionHeading
                title={t("download.title")}
                subtitle={t("download.subtitle")}
            />

            <div className="download-state">{content}</div>
        </div>
    );
};
